import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiExternalLink,
  FiPlus,
  FiShare2,
  FiVideo,
  FiX,
} from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import MoreMenu from './MoreMenu';
import './CrmCalendarView.css';
import { appConfirm } from '../utils/appDialog';

const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const PX_PER_MINUTE = 1.2;
const POPOVER_WIDTH = 300;
const POPOVER_GAP = 10;

/** Google Calendar event palette (classic event colors). */
export const GCAL_EVENT_COLORS = [
  { bg: '#aecbfa', border: '#1a73e8', text: '#174ea6' }, // Blueberry
  { bg: '#f6aea9', border: '#d93025', text: '#8b0000' }, // Tomato
  { bg: '#fde293', border: '#f9ab00', text: '#804600' }, // Banana
  { bg: '#a8dab5', border: '#188038', text: '#0d652d' }, // Sage
  { bg: '#d7aefb', border: '#a142f4', text: '#681da8' }, // Grape
  { bg: '#fdd663', border: '#f9ab00', text: '#804600' }, // Tangerine soft
  { bg: '#c6dafc', border: '#4285f4', text: '#1967d2' }, // Peacock
  { bg: '#e6c9a8', border: '#b06000', text: '#7a4100' }, // Cocoa
  { bg: '#e8eaed', border: '#5f6368', text: '#3c4043' }, // Graphite
  { bg: '#fdcfe8', border: '#e52592', text: '#9c166b' }, // Flamingo
  { bg: '#d4e4fa', border: '#3f51b5', text: '#1a237e' }, // Lavender blue
];

function hashColorIndex(key) {
  const s = String(key || 'event');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % GCAL_EVENT_COLORS.length;
}

export function eventColorFor(ev) {
  return GCAL_EVENT_COLORS[hashColorIndex(ev?.color_key || ev?.id || ev?.title)];
}

function initialsFromName(name, email) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  if (email) return String(email).slice(0, 2).toUpperCase();
  return '?';
}

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() + diff);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function toIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function minutesFromDayStart(date) {
  return date.getHours() * 60 + date.getMinutes() - DAY_START_HOUR * 60;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function formatHourLabel(hour) {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric' });
}

function eventLayout(ev) {
  const start = new Date(ev.starts_at);
  const end = new Date(ev.ends_at || ev.starts_at);
  const startMin = clamp(minutesFromDayStart(start), 0, (DAY_END_HOUR - DAY_START_HOUR) * 60);
  let endMin = clamp(minutesFromDayStart(end), 0, (DAY_END_HOUR - DAY_START_HOUR) * 60);
  if (endMin <= startMin) endMin = startMin + 30;
  return {
    top: startMin * PX_PER_MINUTE,
    height: Math.max(24, (endMin - startMin) * PX_PER_MINUTE),
  };
}

function BookerAvatar({ name, email, avatarUrl, color, size = 'sm' }) {
  const initials = initialsFromName(name, email);
  if (avatarUrl) {
    return (
      <img
        className={`crm-cal-avatar crm-cal-avatar--${size} crm-cal-avatar--photo`}
        src={avatarUrl}
        alt=""
        referrerPolicy="no-referrer"
        title={name || email || ''}
      />
    );
  }
  return (
    <span
      className={`crm-cal-avatar crm-cal-avatar--${size} crm-cal-avatar--initials`}
      style={{ background: color?.border, color: '#fff' }}
      title={name || email || ''}
      aria-hidden
    >
      {initials}
    </span>
  );
}

/**
 * Week + time grid calendar (Google Calendar–style).
 */
export default function CrmCalendarView({
  canManage = false,
  canBook = false,
  onBook,
  onCreateEvent,
  onShare,
  emptyHint,
}) {
  const queryClient = useQueryClient();
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [selected, setSelected] = useState(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, placement: 'right' });
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const from = toIsoDate(weekStart);
  const to = toIsoDate(addDays(weekStart, 7));
  const dayHeight = (DAY_END_HOUR - DAY_START_HOUR) * 60 * PX_PER_MINUTE;
  const todayKey = toIsoDate(new Date());

  const hours = useMemo(
    () => Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i),
    []
  );

  const { data, isLoading, isFetching, error } = useQuery(
    ['crm-calendar', from, to],
    () => api.get('/crm/calendar', { params: { from, to } }).then((r) => r.data),
    { keepPreviousData: true, staleTime: 30_000 }
  );

  useQuery(
    ['crm-calendar-autosync'],
    () => api.post('/google-calendar/sync').then((r) => r.data),
    {
      enabled: canManage,
      staleTime: 5 * 60_000,
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(['crm-calendar']);
      },
    }
  );

  const events = useMemo(() => {
    const list = data?.events || data?.items || [];
    return Array.isArray(list) ? list : [];
  }, [data]);

  const byDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => {
      map[toIsoDate(d)] = [];
    });
    events.forEach((ev) => {
      const key = String(ev.starts_at || '').slice(0, 10);
      if (map[key]) map[key].push(ev);
    });
    return map;
  }, [events, weekDays]);

  const cancelMutation = useMutation((id) => api.delete(`/bookings/${id}`), {
    onSuccess: () => {
      setSelected(null);
      queryClient.invalidateQueries(['crm-calendar']);
      queryClient.invalidateQueries(['crm']);
    },
  });

  const rangeLabel = `${weekDays[0].toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })} – ${weekDays[6].toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;

  const openEvent = (ev, el) => {
    const root = rootRef.current;
    if (!root || !el) {
      setSelected(ev);
      return;
    }
    const rootRect = root.getBoundingClientRect();
    const evRect = el.getBoundingClientRect();
    const spaceRight = rootRect.right - evRect.right;
    const placeRight = spaceRight >= POPOVER_WIDTH + POPOVER_GAP;
    const left = placeRight
      ? evRect.right - rootRect.left + POPOVER_GAP
      : Math.max(8, evRect.left - rootRect.left - POPOVER_WIDTH - POPOVER_GAP);
    const top = Math.max(8, evRect.top - rootRect.top);
    setPopoverPos({
      top,
      left,
      placement: placeRight ? 'right' : 'left',
    });
    setSelected(ev);
  };

  useLayoutEffect(() => {
    if (!selected || !popoverRef.current || !rootRef.current) return;
    const rootRect = rootRef.current.getBoundingClientRect();
    const popRect = popoverRef.current.getBoundingClientRect();
    let { top, left } = popoverPos;
    if (top + popRect.height > rootRect.height - 8) {
      top = Math.max(8, rootRect.height - popRect.height - 8);
    }
    if (left + popRect.width > rootRect.width - 8) {
      left = Math.max(8, rootRect.width - popRect.width - 8);
    }
    if (top !== popoverPos.top || left !== popoverPos.left) {
      setPopoverPos((p) => ({ ...p, top, left }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (!selected) return undefined;
    const onDoc = (e) => {
      if (e.target.closest('.crm-cal-block')) return;
      if (popoverRef.current?.contains(e.target)) return;
      setSelected(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [selected]);

  const selectedColor = selected ? eventColorFor(selected) : null;

  const calendarMenuItems = [
    canManage && onCreateEvent
      ? {
          id: 'new-appointment',
          label: 'New appointment',
          icon: FiPlus,
          onSelect: () => onCreateEvent(),
        }
      : null,
    canBook && onBook
      ? {
          id: 'book',
          label: 'Book appointment',
          icon: FiCalendar,
          onSelect: () => onBook(),
        }
      : null,
    canManage && onShare
      ? {
          id: 'share',
          label: 'Share calendar',
          icon: FiShare2,
          onSelect: () => onShare(),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="crm-cal" aria-busy={isFetching} ref={rootRef}>
      <div className="crm-cal-toolbar">
        <div className="crm-cal-nav">
          <button
            type="button"
            className="crm-cal-nav-btn"
            onClick={() => {
              setSelected(null);
              setAnchor((a) => addDays(a, -7));
            }}
            aria-label="Previous week"
          >
            <FiChevronLeft />
          </button>
          <button
            type="button"
            className="crm-cal-nav-btn crm-cal-nav-btn--today"
            onClick={() => {
              setSelected(null);
              setAnchor(new Date());
            }}
          >
            Today
          </button>
          <button
            type="button"
            className="crm-cal-nav-btn"
            onClick={() => {
              setSelected(null);
              setAnchor((a) => addDays(a, 7));
            }}
            aria-label="Next week"
          >
            <FiChevronRight />
          </button>
          <h2 className="crm-cal-range">
            <FiCalendar aria-hidden /> {rangeLabel}
          </h2>
        </div>
        {calendarMenuItems.length > 0 && (
          <div className="crm-cal-actions">
            <MoreMenu label="Calendar actions" items={calendarMenuItems} />
          </div>
        )}
      </div>

      {error && (
        <p className="form-error" role="alert">
          {error.response?.data?.error || error.message || 'Could not load calendar'}
        </p>
      )}

      {isLoading && !data ? (
        <p className="crm-cal-empty">Loading calendar…</p>
      ) : (
        <div className="crm-cal-timegrid-wrap">
          <div className="crm-cal-timegrid" style={{ '--crm-cal-day-height': `${dayHeight}px` }}>
            <div className="crm-cal-corner" />
            {weekDays.map((day) => {
              const key = toIsoDate(day);
              const isToday = key === todayKey;
              return (
                <div key={`h-${key}`} className={`crm-cal-col-head ${isToday ? 'is-today' : ''}`}>
                  <span className="crm-cal-weekday">
                    {day.toLocaleDateString(undefined, { weekday: 'short' })}
                  </span>
                  <span className={`crm-cal-daynum ${isToday ? 'is-today' : ''}`}>
                    {day.getDate()}
                  </span>
                </div>
              );
            })}

            <div className="crm-cal-hours" style={{ height: dayHeight }}>
              {hours.map((h) => (
                <div
                  key={h}
                  className="crm-cal-hour"
                  style={{ height: 60 * PX_PER_MINUTE }}
                >
                  <span>{formatHourLabel(h)}</span>
                </div>
              ))}
            </div>

            {weekDays.map((day) => {
              const key = toIsoDate(day);
              const dayEvents = byDay[key] || [];
              const isToday = key === todayKey;
              return (
                <div
                  key={key}
                  className={`crm-cal-daycol ${isToday ? 'is-today' : ''}`}
                  style={{ height: dayHeight }}
                >
                  {hours.map((h) => (
                    <div
                      key={h}
                      className="crm-cal-hourline"
                      style={{ top: (h - DAY_START_HOUR) * 60 * PX_PER_MINUTE }}
                    />
                  ))}
                  {dayEvents.map((ev) => {
                    const layout = eventLayout(ev);
                    const color = eventColorFor(ev);
                    const isActive = selected?.id === ev.id;
                    const bookerName = ev.booker_name || ev.candidate_name || ev.consultant_name;
                    return (
                      <button
                        key={ev.id || `${ev.starts_at}-${ev.title}`}
                        type="button"
                        className={`crm-cal-block ${isActive ? 'is-selected' : ''} crm-cal-block--${ev.status || 'scheduled'}`}
                        style={{
                          top: layout.top,
                          height: layout.height,
                          background: color.bg,
                          borderLeftColor: color.border,
                          color: color.text,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEvent(ev, e.currentTarget);
                        }}
                      >
                        <span className="crm-cal-block-main">
                          <BookerAvatar
                            name={bookerName}
                            email={ev.guest_email}
                            avatarUrl={ev.booker_avatar_url}
                            color={color}
                            size="sm"
                          />
                          <span className="crm-cal-block-text">
                            <strong>{ev.title || 'Appointment'}</strong>
                            <span className="crm-cal-block-time">
                              {ev.meeting_url ? <FiVideo aria-hidden /> : null}
                              {new Date(ev.starts_at).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isLoading && events.length === 0 && (
        <p className="crm-cal-empty">
          {emptyHint ||
            'No appointments this week. Use the menu to create an appointment or share your booking page.'}
        </p>
      )}

      {selected && (
        <div
          ref={popoverRef}
          className={`crm-cal-popover crm-cal-popover--${popoverPos.placement}`}
          role="dialog"
          aria-label="Appointment details"
          style={{
            top: popoverPos.top,
            left: popoverPos.left,
            width: POPOVER_WIDTH,
            borderTopColor: selectedColor?.border,
          }}
        >
          <button
            type="button"
            className="crm-cal-popover-close"
            onClick={() => setSelected(null)}
            aria-label="Close"
          >
            <FiX />
          </button>
          <div className="crm-cal-popover-head">
            <BookerAvatar
              name={selected.booker_name || selected.candidate_name}
              email={selected.guest_email}
              avatarUrl={selected.booker_avatar_url}
              color={selectedColor}
              size="lg"
            />
            <div>
              <h3>{selected.title || 'Appointment'}</h3>
              <p className="crm-cal-popover-when">
                {new Date(selected.starts_at).toLocaleString([], {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                {selected.ends_at
                  ? ` – ${new Date(selected.ends_at).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}`
                  : ''}
              </p>
            </div>
          </div>
          {(selected.candidate_name || selected.consultant_name) && (
            <div className="crm-cal-popover-people">
              {selected.candidate_name && (
                <p>
                  <span className="crm-cal-popover-label">Booked by</span>
                  {selected.candidate_name}
                </p>
              )}
              {selected.consultant_name && (
                <p>
                  <span className="crm-cal-popover-label">Consultant</span>
                  {selected.consultant_name}
                </p>
              )}
            </div>
          )}
          <div className="crm-cal-popover-actions">
            {selected.meeting_url ? (
              <>
                <a
                  href={selected.meeting_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary"
                >
                  <FiVideo /> Join with Google Meet
                </a>
                <p className="crm-cal-meet-link">
                  <a href={selected.meeting_url} target="_blank" rel="noreferrer">
                    {selected.meeting_url.replace(/^https?:\/\//, '')}
                  </a>
                </p>
              </>
            ) : (
              <p className="crm-cal-meet-missing">
                No Meet link yet — it appears after Google Calendar creates the event.
              </p>
            )}
            {selected.html_link && (
              <a
                href={selected.html_link}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                <FiExternalLink /> Open in Google Calendar
              </a>
            )}
            {(canManage || canBook) &&
              selected.booking_id &&
              selected.status !== 'cancelled' && (
                <LoadingButton
                  type="button"
                  className="btn btn-danger"
                  loading={cancelMutation.isLoading}
                  onClick={async () => {
                    if (await appConfirm('Cancel this booking?')) {
                      cancelMutation.mutate(selected.booking_id);
                    }
                  }}
                >
                  Cancel
                </LoadingButton>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
